import { useQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';

import { get as getUser } from 'js/lib/user';

import { isSSR } from 'bundles/common/utils/isSSR';
import type { GuidedCourseNextStepsV1MultiGetQuery } from 'bundles/naptimejs/resources/__generated__/GuidedCourseNextStepsV1';
import { COURSE_NEXT_STEP_TYPE_NAME } from 'bundles/unified-description-page-common/constants';
import GuidedCourseNextStepsQuery from 'bundles/unified-description-page-common/queries/GuidedProjectNextStepsQuery';
import { getAllSkippableNextStepsExcept } from 'bundles/unified-description-page-common/utils/nextSteps';

export const useGuidedCourseNextSteps = (courseSlug?: string, skip = false) => {
  const userId = getUser()?.id;
  const stepsToSkip = getAllSkippableNextStepsExcept(new Set(['CourseMaterialNextStep']));

  const { data, loading, refetch } = useQuery<GuidedCourseNextStepsV1MultiGetQuery>(GuidedCourseNextStepsQuery, {
    skip: !courseSlug || !userId || skip,
    ssr: true,
    variables: {
      ids: `${userId}~${courseSlug}`,
      stepsToSkip: stepsToSkip.join(','),
    },
    onError: (errorObj) => {
      if (!isSSR()) {
        Sentry.captureException(errorObj, {
          extra: { message: 'Error calling GuidedCourseNextStepsV1' },
        });
      }
    },
  });

  const courseNextStep = data?.GuidedCourseNextStepsV1?.multiGet?.elements?.[0];
  const courseNextStepTypeName = courseNextStep?.nextStep.typeName;
  const resourcePath =
    courseNextStepTypeName && courseNextStepTypeName === COURSE_NEXT_STEP_TYPE_NAME
      ? courseNextStep.nextStep.definition.item.resourcePath
      : undefined;
  return {
    resourcePath,
    loading,
    refetch,
  };
};
